import { useMemo, useState } from 'react';
import {
  Paper, Table, TableHead, TableRow, TableCell, TableBody, Box, Typography, Chip, Tooltip,
  IconButton, Menu, MenuItem, ListItemIcon, ListItemText,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  COMPONENT_STATUS_LABEL,
  COMPONENT_STATUS_COLOR,
  COMPONENT_STATUS_DESC,
  UNKNOWN_STATUS_LABEL,
  UNKNOWN_STATUS_COLOR,
  EMPTY_MAPPING_TOOLTIP,
} from '../../utils/constants';

// FEAT-002 v0.3 + FEAT-003 — 관점 2: 기능 × 소프트웨어 × 버전 (List + Badge 그룹) + 편집 진입점.
// FEAT-003: Badge 클릭 → onComponentEdit, 행 ⋮ 메뉴 → onAddComponent / onDeleteFeature.
//
// Props:
//   features / softwares / search / selectedStatuses : 기존 (Phase 2)
//   onComponentEdit  : (feature, componentIndex) => void (선택 — 미제공 시 read-only)
//   onAddComponent   : (feature) => void
//   onDeleteFeature  : (feature) => void
export default function FeatureSwVersionMatrix({
  features,
  softwares,
  search,
  selectedStatuses,
  onComponentEdit,
  onAddComponent,
  onDeleteFeature,
}) {
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuFeature, setMenuFeature] = useState(null);
  const editable = !!(onComponentEdit || onAddComponent || onDeleteFeature);

  const swMetaMap = useMemo(() => {
    const map = {};
    softwares.forEach((s) => { map[s.id] = s; });
    return map;
  }, [softwares]);

  const filteredFeatures = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return features;
    return features.filter((f) =>
      `${f.id} ${f.name || ''} ${f.owner || ''}`.toLowerCase().includes(q),
    );
  }, [features, search]);

  if (filteredFeatures.length === 0) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">검색 결과 없음</Typography>
      </Paper>
    );
  }

  const hasStatusFilter = selectedStatuses.length > 0;
  const openMenu = (e, feature) => { setMenuAnchor(e.currentTarget); setMenuFeature(feature); };
  const closeMenu = () => { setMenuAnchor(null); setMenuFeature(null); };

  return (
    <Paper>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: { xs: 220, md: 280 } }}>
              <Typography variant="caption" sx={{ fontWeight: 700 }}>기능</Typography>
            </TableCell>
            <TableCell>
              <Typography variant="caption" sx={{ fontWeight: 700 }}>구성 소프트웨어 × 버전 × 상태</Typography>
            </TableCell>
            {editable && <TableCell sx={{ width: 50 }} />}
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredFeatures.map((f) => {
            const components = f.components || [];
            const visible = hasStatusFilter
              ? components.filter((c) => selectedStatuses.includes(c.status))
              : components;

            return (
              <TableRow key={f.id} hover sx={{ verticalAlign: 'top' }}>
                <TableCell sx={{ py: 1.5 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{f.name || f.id}</Typography>
                  <Typography
                    variant="caption"
                    sx={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 11, color: 'text.secondary' }}
                  >
                    {f.id}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                    {f.owner && (
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>{f.owner}</Typography>
                    )}
                    {f.status && (
                      <Chip
                        label={f.status}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: 10, height: 18 }}
                      />
                    )}
                  </Box>
                </TableCell>
                <TableCell sx={{ py: 1.5 }}>
                  {components.length === 0 ? (
                    <Tooltip title={EMPTY_MAPPING_TOOLTIP} arrow placement="top">
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'text.disabled',
                          fontStyle: 'italic',
                          cursor: 'help',
                          display: 'inline-block',
                          px: 1,
                          py: 0.5,
                          bgcolor: 'action.hover',
                          borderRadius: 1,
                        }}
                      >
                        ⓘ 매핑 미입력
                      </Typography>
                    </Tooltip>
                  ) : visible.length === 0 ? (
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'text.disabled',
                        fontStyle: 'italic',
                        display: 'inline-block',
                        px: 1,
                        py: 0.5,
                        bgcolor: 'action.hover',
                        borderRadius: 1,
                      }}
                    >
                      선택한 상태 필터 결과 없음 ({components.length}개 Badge 모두 미매칭)
                    </Typography>
                  ) : (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {visible.map((c, i) => {
                        const isKnown = c.status in COMPONENT_STATUS_LABEL;
                        const statusLabel = isKnown ? COMPONENT_STATUS_LABEL[c.status] : UNKNOWN_STATUS_LABEL;
                        const statusColor = isKnown ? COMPONENT_STATUS_COLOR[c.status] : UNKNOWN_STATUS_COLOR;
                        const meta = swMetaMap[c.product];
                        const version = c.version || 'TBD';
                        const label = `${c.product} ${version} · ${statusLabel}`;
                        const tipBody = [
                          meta?.name ? `이름: ${meta.name}` : null,
                          `상태: ${statusLabel}`,
                          isKnown ? COMPONENT_STATUS_DESC[c.status] : `알 수 없는 상태값: ${c.status}`,
                          c.notes ? `메모: ${c.notes}` : null,
                          editable ? '(클릭 → 편집)' : null,
                        ].filter(Boolean).join('\n');

                        if (c.status && !isKnown) {
                          // eslint-disable-next-line no-console
                          console.warn(`[FeatureSwVersionMatrix] unknown status: ${c.status}`);
                        }

                        // 실제 components 배열에서의 index (visible 필터 적용 시 다름)
                        const realIdx = components.indexOf(c);

                        return (
                          <Tooltip
                            key={`${c.product}-${version}-${i}`}
                            title={<Box sx={{ whiteSpace: 'pre-line' }}>{tipBody}</Box>}
                            arrow
                            placement="top"
                          >
                            <Chip
                              label={label}
                              size="small"
                              color={statusColor}
                              variant="filled"
                              clickable={!!onComponentEdit}
                              onClick={onComponentEdit ? () => onComponentEdit(f, realIdx) : undefined}
                              sx={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 11 }}
                            />
                          </Tooltip>
                        );
                      })}
                      <Typography variant="caption" sx={{ color: 'text.secondary', alignSelf: 'center', ml: 1 }}>
                        ({visible.length}{hasStatusFilter && visible.length !== components.length ? `/${components.length}` : ''}개)
                      </Typography>
                    </Box>
                  )}
                </TableCell>
                {editable && (
                  <TableCell sx={{ py: 1.5, textAlign: 'right' }}>
                    <IconButton size="small" onClick={(e) => openMenu(e, f)}>
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      {editable && (
        <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={closeMenu}>
          {onAddComponent && (
            <MenuItem onClick={() => { onAddComponent(menuFeature); closeMenu(); }}>
              <ListItemIcon><AddIcon fontSize="small" /></ListItemIcon>
              <ListItemText>구성 SW 추가</ListItemText>
            </MenuItem>
          )}
          {onDeleteFeature && (
            <MenuItem onClick={() => { onDeleteFeature(menuFeature); closeMenu(); }}>
              <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
              <ListItemText sx={{ color: 'error.main' }}>이 Feature 삭제</ListItemText>
            </MenuItem>
          )}
        </Menu>
      )}
    </Paper>
  );
}
